import { useEffect, useState, useRef, type FC } from "react";
import type { ColorStop } from "./WebGLBackground";
import "./SmoothThresholdBackground.css";

interface SmoothThresholdBackgroundProps {
  colorStops: ColorStop[];
  transitionDuration?: number; // in seconds
  threshold?: number; // how close to a stop before triggering (0-1)
  easing?: string; // CSS easing function
  useCSSTiming?: boolean; // Use CSS variable timing instead of prop values
  resetKey?: string; // Key to trigger reset on route change
  cssVariableName?: string; // Name of the CSS variable to update
}

export const SmoothThresholdBackground: FC<SmoothThresholdBackgroundProps> = ({
  colorStops,
  transitionDuration = 0.4,
  threshold = 0.01, // trigger when within 1% of a stop
  easing = "cubic-bezier(0.3, 1, 0.7, 1)", // Studyhall.design timing
  useCSSTiming = true, // Default to using CSS variables
  resetKey,
  cssVariableName = "--background-color", // Default CSS variable name
}) => {
  const [backgroundColor, setBackgroundColor] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const currentStopIndexRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  // Convert color to CSS format
  const colorToCSS = (color: string | number[]): string => {
    if (typeof color === "string") {
      return color;
    }
    // Convert from 0-1 range to 0-255
    const r = Math.round(color[0] * 255);
    const g = Math.round(color[1] * 255);
    const b = Math.round(color[2] * 255);
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Update both state and CSS variable
  const updateColor = (newColor: string) => {
    setBackgroundColor(newColor);

    // Extract RGB values for rgba usage
    const rgbMatch = newColor.match(/rgb?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbMatch) {
      const [_, r, g, b] = rgbMatch;
      document.documentElement.style.setProperty(`${cssVariableName}-rgb`, `${r}, ${g}, ${b}`);
    }
  };

  useEffect(() => {
    // Set initial color
    if (colorStops.length > 0) {
      updateColor(colorToCSS(colorStops[0].color));
    }
  }, []);

  // Reset to first color when route changes
  useEffect(() => {
    if (resetKey && colorStops.length > 0) {
      currentStopIndexRef.current = 0;
      setIsTransitioning(false);
      updateColor(colorToCSS(colorStops[0].color));
    }
  }, [resetKey, colorStops]);

  useEffect(() => {
    let ticking = false;

    const updateBackground = () => {
      // Calculate scroll progress (0 to 1)
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollHeight > 0 ? Math.min(window.scrollY / scrollHeight, 1) : 0;

      // Find which stop we should be at
      let targetStopIndex = 0;
      for (let i = colorStops.length - 1; i >= 0; i--) {
        if (progress >= colorStops[i].progress - threshold) {
          targetStopIndex = i;
          break;
        }
      }

      // Check if we need to change color
      if (targetStopIndex !== currentStopIndexRef.current) {
        currentStopIndexRef.current = targetStopIndex;
        const newColor = colorToCSS(colorStops[targetStopIndex].color);

        // Trigger transition
        setIsTransitioning(true);
        updateColor(newColor);

        // Reset transition state after duration
        setTimeout(() => {
          setIsTransitioning(false);
        }, transitionDuration * 1000);
      }

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        rafRef.current = requestAnimationFrame(updateBackground);
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Initial update
    updateBackground();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [colorStops, threshold, transitionDuration]);

  return (
    <div
      className={`smooth-threshold-background ${isTransitioning ? "transitioning" : ""}`}
      style={{
        backgroundColor,
        transition: useCSSTiming
          ? undefined // Let CSS handle the transition
          : isTransitioning
            ? `background-color ${transitionDuration}s ${easing}`
            : "none",
      }}
    />
  );
};
